import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateCertificate } from "../../../api/certificates";
import useCertificateOfOriginStore from "../../../stores/certificateOfOriginStore";
import apiErrorToString from "../../../utils/response";

export default function useSummary() {
  const navigate = useNavigate();
  const selectedBomIds = useCertificateOfOriginStore((s) => s.selectedBomIds);
  const [errorMsg, setErrorMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { mutate: createCertificate, isError, error, data: certificateData } = useCreateCertificate();
  const certifier = useCertificateOfOriginStore((s) => s.certifier);
  const exporter = useCertificateOfOriginStore((s) => s.exporter);
  const importer = useCertificateOfOriginStore((s) => s.importer);
  const producerDataType = useCertificateOfOriginStore((s) => s.producerDataType);
  const producers = useCertificateOfOriginStore((s) => s.producers);

  const handleSubmit = useCallback(() => {
    setSubmitting(true);
    setErrorMsg(null);
    const requestBody = {
      certifying_company: certifier.company,
      certifier_name: certifier.name,
      certifier_type: certifier.role,
      certifier_address: certifier.address,
      certifier_phone_number: certifier.phoneNumber,
      certifier_email: certifier.emailAddress,
      effective_date: certifier.effectiveDate,
      expiration_date: certifier.expirationDate,
      producer_data_type: producerDataType,
      producers: producers.map((producer) => ({
        name: producer.name,
        email: producer.email,
        phone: producer.phoneNumber,
        address: producer.address,
      })),
      importer_name: importer.name,
      importer_email: importer.email,
      importer_phone_number: importer.phoneNumber,
      importer_address: importer.address,
      qualified_good_ids: selectedBomIds,
    };

    if (exporter.name && exporter.name.length > 0) {
      requestBody.exporter_name = exporter.name;
      requestBody.exporter_email = exporter.email;
      requestBody.exporter_phone_number = exporter.phoneNumber;
      requestBody.exporter_address = exporter.address;
    }

    createCertificate(requestBody);
  }, [certifier, exporter, importer, producerDataType, producers, selectedBomIds, createCertificate]);

  useEffect(() => {
    if (isError && error) {
      if (error.data) {
        setErrorMsg(apiErrorToString(error.data));
      } else {
        setErrorMsg("Unknown error! Please try again.");
      }
    }

    return () => {
      setErrorMsg(null);
    };
  }, [isError, error]);

  useEffect(() => {
    if (certificateData) {
      setSubmitting(false);
      navigate(`/dashboard/certificate-of-origin/${certificateData.message}`, { replace: true });
    }
  }, [navigate, certificateData]);

  return useMemo(() => ({ handleSubmit, submitting, errorMsg }), [handleSubmit, submitting, errorMsg]);
}
